/* eslint-disable @typescript-eslint/no-explicit-any */
import { create } from 'apisauce';
import { toast } from 'react-toastify';
import { camelCase, getCookie } from 'utils/common.util';
import { ResponseType } from 'typings/common.type';
import { RequestOtpV1Url, RefreshTokenUrl } from 'routes/account.routes';
import i18n from 'locales/fa.json';
import {
  logoutAction,
  setUaaCookie,
  paymentMessage,
} from 'utils/component.util';
import { ChangePasswordUrl } from 'routes/profile.routes';

const baseURL =
  process.env.NEXT_PUBLIC_BASE_URL || 'https://core.uat.enigmainvesting.ir';
const liveDataApiUrl =
  process.env.NEXT_PUBLIC_LIVE_DATA_API_URL ||
  'https://live.uat.enigmainvesting.ir';
const liveDataBaseURL =
  process.env.NEXT_PUBLIC_LIVE_DATA_WS_URL ||
  'wss://live.uat.enigmainvesting.ir';
const panelURL = process.env.NEXT_PUBLIC_PANEL_URL || 'https://panel.enigma.ir';
const appVersion = process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0';
const siteUrl =
  process.env.NEXT_PUBLIC_SITE_URL || 'https://pwa.uat.enigmainvesting.ir';
const otpAction = 'OTP';
const nodeEnv = process.env.NEXT_PUBLIC_ENV;
const gtmId = process.env.NEXT_PUBLIC_GTM_ID;
const recaptchaKey =
  process.env.NEXT_PUBLIC_RECAPTCHA_KEY ||
  '6LfvFqoZAAAAANqCMlaajgkmye7bLGAUzNF8dz4L';
export const BUILD_ID = process.env.NEXT_PUBLIC_BUILD_ID;

const request = ((): any => {
  const headers = {
    'Content-Type': 'application/json',
    'Accept-Language': 'fa',
    'App-Name': 'pwa',
  };

  return create({ baseURL, headers });
})();

const getAccessToken = async (): Promise<ResponseType<{ access: string }>> => {
  const refresh = getCookie('pwa_refresh_token');
  const newToken = await request.post(RefreshTokenUrl(), { refresh });
  return newToken;
};

// we try to add access token to the request's header, in case there is no access token we try to get new one.
// if we can't handle access token here, 'interceptors.response' will handle it.
request.axiosInstance.interceptors.request.use(
  async (req: { url: string; headers: { Authorization: string } }) => {
    if (typeof window !== 'undefined') {
      // Identify the app running on pwa or twa, and set correct App-Name
      const isTwa = window?.localStorage.getItem('isTwa') === 'true';
      if (isTwa) {
        req.headers['App-Name'] = `android`;
      }
    }
    const access = getCookie('pwa_access_token');
    const refresh = getCookie('pwa_refresh_token');
    if (access) {
      req.headers.Authorization = `Bearer ${access}`;
    } else if (refresh && !req.url.includes('api/v1/account/refresh')) {
      const newToken = await getAccessToken();
      if (newToken.ok) {
        await setUaaCookie(newToken.data.data.access, refresh);
        req.headers.Authorization = `Bearer ${newToken.data.data.access}`;
      }
    }
    return req;
  },
);

/*
 * handle unexpected error here.
 * access token expire every 30 minute.
 * if access token expire we get an 401 error on api requests.
 * here we get new access token with refresh token and update it.
 * if refresh token expired too, Login would redirect to Login page.
 */
request.axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: {
    response: { status: number };
    config: { headers: any; url: string | string[] };
  }) => {
    if (error.response.status !== 401) {
      return error;
    }
    // if refresh request status is 401 redirect to Login
    if (error.config.url.includes('api/v1/account/refresh')) {
      if (error.response.status === 401) await logoutAction(false, true);
      return Promise.reject(error);
    }

    // when logout request received 401 error should not request for new refresh token
    if (error.config.url.includes('api/v1/account/logout')) {
      return error;
    }

    const refresh = getCookie('pwa_refresh_token');
    const originalRequest = error.config;
    if (refresh && error.response.status === 401) {
      const newToken = await getAccessToken();
      if (newToken.ok) {
        // set header and cookie with new access token
        setUaaCookie(newToken.data.data.access, refresh);
        // retry original request
        originalRequest.headers.Authorization = `Bearer ${newToken.data.data.access}`;
        return request.axiosInstance.request(originalRequest);
      }
      // when refresh token request received 429 error must be logout
      return error;
    }
    await logoutAction(false, true);

    return error;
  },
);

// for show notification on errors.
export const requestMonitor = (response: {
  originalError: { response: { data: { message: string } } };
  config: {
    params: { showSuccessMessage: boolean; showErrorMessage: boolean };
    url: string;
  };
  status: number;
  problem: string | number;
  data: { message: string };
  ok: any;
}): void => {
  const originalMessage = response?.originalError?.response?.data?.message;
  const showSuccessMessage =
    response?.config?.params?.showSuccessMessage !== false;
  const showErrorMessage = response?.config?.params?.showErrorMessage !== false;

  const successList = [RequestOtpV1Url(), ChangePasswordUrl()].some(
    (k) => k === response.config.url,
  );

  const messages = (originalMessage &&
    (typeof originalMessage === 'string'
      ? [originalMessage]
      : Object.keys(originalMessage).map((item) =>
          typeof originalMessage[item] === 'string'
            ? originalMessage[item]
            : originalMessage[item][0],
        ))) || [
    i18n.errors[camelCase(response.problem?.toString()?.toLowerCase())],
  ];

  if (
    (response.status === 201 && showSuccessMessage) ||
    (response.status === 200 && successList)
  )
    toast.success(response.data.message || i18n.global.messages.created);

  if (response?.status === 429) toast.error(i18n.global.messages.throttle);

  if (!response?.ok) {
    if (response?.status === 401) {
      logoutAction(); // for check whether refresh token has expired or not
    } else if (response?.status === 402 || response.status === 403) {
      toast.error(paymentMessage());
    } else showErrorMessage && messages.forEach((key) => toast.error(key));
  }
};

export default request;
export {
  baseURL,
  liveDataApiUrl,
  liveDataBaseURL,
  otpAction,
  recaptchaKey,
  panelURL,
  appVersion,
  siteUrl,
  nodeEnv,
  gtmId,
};
